import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import cn from "classnames";
import { useIntl } from "react-intl";

import Identified from "@mapmycustomers/shared/types/base/Identified";
import { Company, Deal, Person } from "@mapmycustomers/shared/types/entity";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import { Button, LoadingSpinner, SearchField, SearchFieldProps } from "@mapmycustomers/ui";

import NoData from "@app/component/preview/components/NoData";

import AssociationRow from "./AssociationRow";
import AssociationRowWithPrimaryEntity from "./AssociationRowWithPrimaryEntity";
import styles from "./Associations.module.scss";
import messages from "./messages";

interface Props<T extends Identified> {
  allowUnsetPrimaryEntity?: boolean;
  alreadyAssociatedMessage: ReactNode;
  assignedRecords?: T[];
  associateWith?: T["id"];
  cantAssociateWithSelfMessage: ReactNode;
  className?: string;
  disableNonEditableEntities?: boolean;
  entities?: T[];
  extras?: ReactNode;
  filtered?: number;
  getListTitle?: (count: number, total: number) => string;
  loading?: boolean;
  multiselect?: boolean;
  noDataImageSrc?: string;
  notEditableMessage?: ReactNode;
  onChange?: (selectedRows: Identified["id"][]) => void;
  onChangeQuery?: (query: string) => void;
  onSearch?: (text: string) => void;
  onUpdatePrimaryEntity?: (primaryEntityId?: Identified["id"]) => void;
  placeholder?: string;
  primaryEntityId?: Identified["id"];
  query: string;
  searchFieldProps?: SearchFieldProps;
  suggestedEntities?: T[];
  supportsPrimaryEntity?: boolean;
  total?: number;
  value?: T["id"][];
}

type SupportedEntities = Company | Deal | Person;

const Associations = <T extends SupportedEntities>({
  allowUnsetPrimaryEntity = true,
  alreadyAssociatedMessage,
  assignedRecords,
  associateWith,
  cantAssociateWithSelfMessage,
  className,
  disableNonEditableEntities,
  entities,
  extras,
  filtered,
  getListTitle,
  loading,
  multiselect,
  noDataImageSrc,
  notEditableMessage,
  onChange,
  onChangeQuery,
  onSearch,
  onUpdatePrimaryEntity,
  placeholder,
  primaryEntityId,
  query,
  searchFieldProps,
  suggestedEntities,
  supportsPrimaryEntity,
  total,
  value,
}: Props<T>) => {
  const intl = useIntl();
  const [selectedRows, setSelectedRows] = useState<Set<Identified["id"]>>(new Set(value ?? []));
  const [showAllEntities, setShowAllEntities] = useState(false);

  useEffect(() => {
    setSelectedRows(new Set(value ?? []));
  }, [value]);

  const assignedRows = useMemo(
    () => new Set(assignedRecords?.map(({ id }) => id) ?? []),
    [assignedRecords]
  );

  const primaryEntityName = useMemo(
    () => entities?.find(({ id }) => id === primaryEntityId)?.name,
    [entities, primaryEntityId]
  );

  const notifyAboutSearch = useDebouncedCallback(
    [
      useCallback(
        (searchText: string) => {
          onSearch?.(searchText.trim());
        },
        [onSearch]
      ),
      500,
    ],
    [onSearch]
  );

  const handleSetQuery = useCallback(
    (query: string) => {
      onChangeQuery?.(query);
      notifyAboutSearch(query);
    },
    [notifyAboutSearch, onChangeQuery]
  );

  const handleChange = useCallback(
    (entityId: Identified["id"], value: boolean) => {
      const updatedSelection = new Set(multiselect ? selectedRows : []);

      if (value) {
        updatedSelection.add(entityId);
      } else {
        updatedSelection.delete(entityId);
      }
      setSelectedRows(updatedSelection);

      onChange?.(Array.from(updatedSelection));
    },
    [multiselect, onChange, selectedRows, setSelectedRows]
  );

  const hasQuery = query?.trim().length > 0;

  // The default (pre-"Show all", unsearched) view normally lists the suggested records.
  // When there are no suggestions, fall back to the already-assigned records so reopening
  // the picker still shows the current selection instead of an empty state.
  const defaultEntities =
    suggestedEntities && suggestedEntities.length === 0
      ? (assignedRecords ?? [])
      : suggestedEntities;
  const showingSuggestedView = Boolean(!showAllEntities && suggestedEntities && !hasQuery);
  const visibleEntities = showingSuggestedView ? defaultEntities : entities;

  return (
    <section className={cn(styles.container, className)}>
      <div className={styles.searchBar}>
        <SearchField
          {...searchFieldProps}
          allowClear={hasQuery}
          autoFocus
          className={cn(styles.searchField, searchFieldProps?.className)}
          onChange={handleSetQuery}
          placeholder={placeholder}
          size="large"
          value={query}
        />

        {extras && <div>{extras}</div>}
      </div>

      {showAllEntities && (
        <div className={styles.sortOrder}>
          {getListTitle?.(filtered ?? entities?.length ?? total ?? 0, total ?? 0) ??
            intl.formatMessage(messages.allRecords, {
              count: filtered ?? entities?.length ?? total ?? 0,
              total,
            })}
        </div>
      )}

      {suggestedEntities && !showAllEntities && !hasQuery && (
        <div className={styles.sortOrder}>
          {intl.formatMessage(messages.associated, {
            suggested: defaultEntities?.length ?? 0,
            total,
          })}
        </div>
      )}

      {showingSuggestedView && (defaultEntities?.length ?? 0) === 0 ? (
        <></>
      ) : (
        <div className={styles.listContainer}>
          <div className={styles.list}>
            {loading ? (
              <LoadingSpinner />
            ) : visibleEntities?.length ? (
              <>
                {visibleEntities?.map((entity) => {
                  const notEditable =
                    !!disableNonEditableEntities && entity.accessStatus?.update === false;
                  const tooltipMessage = notEditable
                    ? notEditableMessage
                    : entity.id === associateWith
                      ? cantAssociateWithSelfMessage
                      : assignedRows.has(entity.id)
                        ? alreadyAssociatedMessage
                        : null;
                  return supportsPrimaryEntity ? (
                    <AssociationRowWithPrimaryEntity
                      allowUnsetPrimary={allowUnsetPrimaryEntity}
                      disabled={notEditable}
                      entity={entity}
                      key={entity.id}
                      multiselect={multiselect}
                      onChange={handleChange}
                      onUpdatePrimaryEntity={onUpdatePrimaryEntity}
                      primaryEntityId={primaryEntityId}
                      primaryEntityName={primaryEntityName}
                      selected={selectedRows.has(entity.id)}
                      selectedRowsCount={selectedRows.size}
                      tooltipMessage={tooltipMessage}
                    />
                  ) : (
                    <AssociationRow
                      disabled={notEditable}
                      entity={entity}
                      key={entity.id}
                      multiselect={multiselect}
                      onChange={handleChange}
                      selected={selectedRows.has(entity.id)}
                      tooltipMessage={tooltipMessage}
                    />
                  );
                })}
              </>
            ) : (
              <NoData
                imageSrc={noDataImageSrc ?? ""}
                showTitle={false}
                text={intl.formatMessage(messages.noRecords)}
              />
            )}
          </div>
        </div>
      )}
      {showingSuggestedView && (
        <div className={styles.showAll}>
          <span className={styles.cantFind}>
            {intl.formatMessage(
              (defaultEntities?.length ?? 0) > 0 ? messages.cantFind : messages.noAssociatedRecords
            )}
          </span>
          <Button onClick={() => setShowAllEntities(true)} size="small" type="link">
            {intl.formatMessage(messages.showAll)}
          </Button>
        </div>
      )}
    </section>
  );
};

export default Associations;
